import { heroTakeaway, keyTakeawaySchema } from "@/data/keyTakeaways";
import { useState } from "react";
import { lookupSlug } from "@/lib/slugLookup";
import { Navigate } from "@/lib/router-compat";
import { useStableLocation } from "@/hooks/useStableLocation";
import SEOHead from "@/components/SEOHead";
import Navbar from "@/components/Navbar";
import { DIVISION_LOGOS } from "@/lib/divisionLogos";
import CommercialBrowseSidebar from "@/components/services/CommercialBrowseSidebar";
import ScrollToTop from "@/components/ScrollToTop";
import Footer from "@/components/Footer";
import ContactCTA from "@/components/ContactCTA";
import WebinarRegistrationForm from "@/components/webinar/WebinarRegistrationForm";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { getServiceBySlug } from "@/data/servicesData";
import ServiceHero from "@/components/services/ServiceHero";
import ServiceWhyQbd from "@/components/services/ServiceWhyQbd";
import ServiceRelatedContent from "@/components/services/ServiceRelatedContent";
import GenericOfferingContent from "@/components/services/GenericOfferingContent";
import BreadcrumbJsonLd from "@/components/BreadcrumbJsonLd";
import AiRoadmapSection from "@/components/services/AiRoadmapSection";
import AITransformationContent from "@/components/services/AITransformationContent";
import { ArrowRight, ArrowDown, Users, ShieldAlert, Scale, TrendingDown, ShieldCheck, Lock, Server, Database, Cpu, Truck, GraduationCap, RefreshCw, Handshake, Check, Mail, Phone, MapPin, ChevronDown } from "lucide-react";
import { z } from "zod";
import Reveal from "@/components/services/Reveal";
import ReviewItemsPanel from "@/components/services/ReviewItemsPanel";


import { swOfferingData } from "@/data/swOfferingData";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useLanguage, langPrefix } from "@/hooks/useLanguage";
import { getSwOfferingI18n } from "@/i18n/swOfferings";
import { getServiceContent } from "@/i18n/serviceDetail";
import { localizeHref } from "@/lib/localizeHref";
import { SITE_URL as SITE, DEFAULT_OG_IMAGE } from "@/config/site";

interface FaqItem { q: string; a: string; }

const contactSchema = z.object({
  firstName: z.string().trim().min(1, "First name is required").max(100),
  lastName: z.string().trim().min(1, "Last name is required").max(100),
  company: z.string().trim().min(1, "Company is required").max(200),
  email: z.string().trim().email("Enter a valid business email").max(255),
  country: z.string().min(1, "Select a country"),
  message: z.string().trim().min(1, "Please tell us what you are trying to solve").max(2000),
  consent: z.boolean().refine((v) => v === true, { message: "Please accept the privacy policy" }),
});

const AI_FAQS: FaqItem[] = [
  {
    q: "Does this only apply to GxP systems?",
    a: "No. The program covers your whole AI estate. The classification model then decides how much control each use case needs. A meeting summariser and a model that touches batch release do not get the same treatment, and treating them the same is how programs stall.",
  },
  {
    q: "How does this relate to computer system validation?",
    a: "Validation is one control inside the program, not the program itself. We apply risk-based lifecycle assurance so that validation effort goes where the risk sits. Most AI use cases in a pharma company need governance and monitoring, not a full CSV package.",
  },
  {
    q: "What is ISO/IEC 42001 and do we need it?",
    a: "ISO/IEC 42001 is the management system standard for artificial intelligence. Certification is optional today. Readiness is not, because it shapes what auditors and customers will ask you for. Phase 5 of our roadmap includes a readiness assessment.",
  },
  {
    q: "Can you work with the AI tools we have already bought?",
    a: "Yes. Phase 1 starts with an inventory of what is already running. Tools that fit the approved architecture stay. Tools that do not get remediated or retired through a controlled decision, not a memo.",
  },
  {
    q: "How quickly do we see something concrete?",
    a: "Phase 1 runs in one to four weeks and ends with a charter, an inventory and temporary controls. You have defensible answers for an inspector within the first month.",
  },
  {
    q: "Who do we need to involve?",
    a: "An executive sponsor, quality, IT and cyber security, and one business owner per candidate use case. We supply the program structure and the specialist capacity.",
  },
];

const AI_REVIEW_ITEMS = [
  { id: "proof-card-us-go-live", label: "Proof metric: full eQMS live in six weeks (client name)" },
  { id: "proof-card-14-sites", label: "Proof metric: 14-site AI transformation program (client name)" },
  { id: "proof-testimonial", label: "Testimonial: quote, portrait and reference contact" },
];

const COUNTRIES = [
  "Belgium", "France", "Germany", "Italy", "Netherlands", "Spain", "Switzerland",
  "United Kingdom", "United States", "Other"
];


/* Map Spanish URL slugs → English content keys */
const ES_SLUG_MAP: Record<string, string> = {
  "cumplimiento-de-software-operativo": "operational-software-compliance",
  "digitalización": "digitalization",
  "conformidad-del-software-de-dispositivos-médicos": "medical-device-software-compliance",
  "cumplimiento-ia": "ai-ml-compliance",
  "gobernanza-de-datos": "data-governance",
  "validación-de-software-y-sistemas-informáticos": "software-validation",
  "integridad-de-datos": "data-integrity",
  "implementación-de-software": "software-implementations",
  "cualificación-de-la-infraestructura-informática": "it-infrastructure-qualification",
  "validación-sin-papel": "paperless-validation",
};

const getOfferingPath = (pathname: string): string | null => {
  const match = pathname.match(/\/(?:services\/software-solutions-services|servicios\/software-solutions-services)\/([^/]+)$/);
  if (!match) return null;
  const raw = decodeURIComponent(match[1]);
  const enSlug = lookupSlug(ES_SLUG_MAP, raw);
  return `/en/services/software-solutions-services/${enSlug}`;
};

const offeringPages: Record<string, {
  title: string;
  subtitle: string;
  description: string;
  contentKey: string;
  ogImage?: string;
  contentTags?: string[];
  pinnedSlugs?: { type: "blog" | "case-study" | "white-paper" | "webinar" | "regulatory-update" | "news" | "podcast"; slug: string }[];
  faqItems?: FaqItem[];
  noIndex?: boolean;
}> = {
  "/en/services/software-solutions-services/operational-software-compliance": {
    title: "Operational Software Compliance",
    subtitle: "Maintain compliance throughout the software lifecycle",
    description: "Maintaining compliance with regulatory standards is no small task. QbD Group's operational software compliance services ensure your software systems meet industry standards and regulations, enhancing data security and system reliability.",
    contentKey: "operational-software-compliance",
    contentTags: ["Software Solutions & Services"],
  },
  "/en/services/software-solutions-services/digitalization": {
    title: "Digitalization in Life Sciences",
    subtitle: "Modernize and streamline your operations with compliant digital solutions",
    description: "QbD Group helps life science companies modernize and streamline their operations. Our tailored digitalization solutions ensure a smooth transition to more efficient, agile, and compliant processes, supporting data integrity and long-term growth.",
    contentKey: "digitalization",
    contentTags: ["Software Solutions & Services"],
    faqItems: [
      { q: "What is digitalization in life sciences?", a: "Digitalization in life sciences is the process of converting information and processes from analog to digital formats. By leveraging cutting-edge technologies, organizations transform manual and paper-based workflows into compliant, automated, and data-driven systems." },
      { q: "How does QbD Group approach digitalization projects?", a: "We follow a structured methodology: needs assessment, vendor and product selection, solution design, software implementation, validation and compliance, and change management — ensuring a seamless transition at every stage." },
      { q: "Is QbD Group vendor-neutral when recommending digital solutions?", a: "Yes. We take a technology-agnostic approach, comparing a wide range of solutions — from simple tools to complex enterprise platforms — to recommend the best fit based on your specific needs, risk profile, and budget." },
      { q: "How do you ensure regulatory compliance during digitalization?", a: "We validate each system in line with Annex 11, 21 CFR Part 11, and GAMP5 standards to guarantee data integrity and regulatory compliance throughout the digital transformation." },
      { q: "What is the partnership between QbD Group and delaware?", a: "Through our strategic partnership with delaware, we combine deep life sciences expertise with best-in-class digital technologies. Together, we help organizations implement powerful systems like SAP, fully validated, compliant, and aligned with your processes from day one." },
    ],
  },
  "/en/services/software-solutions-services/digitalization-AI": {
    title: "AI Transformation Program for Pharma and MedTech",
    subtitle: "From scattered AI pilots to governed, inspection-ready AI adoption across every site",
    description: "Your people already use AI. Your organisation does not. We take you from scattered pilots to governed, inspection-ready AI adoption across every site.",
    contentKey: "digitalization-AI",
    contentTags: ["Software Solutions & Services"],
    faqItems: AI_FAQS,
    pinnedSlugs: [
      { type: "webinar", slug: "regulatory-evolution-what-ai-means-for-gmp-manufacturing" },
      { type: "webinar", slug: "ai-in-life-sciences-gxp-compliance-on-demand" },
      { type: "blog", slug: "why-most-ai-pilots-fail-regulated-environments" },
      { type: "blog", slug: "ai-life-sciences-compliant-reality" },
      { type: "blog", slug: "why-ai-too-generic-for-regulatory-conversations" },
    ],
    noIndex: true,
  },
  "/en/services/software-solutions-services/ai-transformation": {
    title: "AI Transformation Program",
    subtitle: "Pragmatic AI adoption for pharma and MedTech",
    description: "Most AI messaging promises everything and proves nothing. We start where you actually are on the road from standardised to AI-enabled, and build your realistic next step, validated and ready for inspection.",
    contentKey: "ai-transformation",
    contentTags: ["Software Solutions & Services"],
  },



  "/en/services/software-solutions-services/medical-device-software-compliance": {
    title: "Medical Device Software Compliance",
    subtitle: "Ensure your software meets MDR, IVDR, FDA, and ISO standards",
    description: "Our Medical Device Software Compliance service helps medical device and IVD companies ensure their software aligns with MDR, IVDR, FDA, ISO 13485, ISO 14971, IEC 62304, and IEC 82304. With tailored frameworks and expert guidance, we simplify compliance.",
    contentKey: "medical-device-software-compliance",
    contentTags: ["Software Solutions & Services", "Medical Devices"],
    pinnedSlugs: [{ type: "webinar", slug: "clinical-evidence-for-samds-ivd" }],
  },
  "/en/services/software-solutions-services/ai-ml-compliance": {
    title: "AI/ML Compliance",
    subtitle: "Navigate AI regulations with confidence",
    description: "In the rapidly evolving world of AI and machine learning, developing software solutions that meet regulatory standards is crucial. QbD Group offers comprehensive AI/ML compliance support to help you shorten your path to market.",
    contentKey: "ai-ml-compliance",
    contentTags: ["Software Solutions & Services"],
  },
  "/en/services/software-solutions-services/data-governance": {
    title: "Data Governance",
    subtitle: "Strategic management of your data assets for compliance and efficiency",
    description: "Are you concerned about keeping up with complex and ever-changing data regulations? QbD Group offers data governance support helping you be compliant with the latest standards and regulations and shorten your path to market.",
    contentKey: "data-governance",
    contentTags: ["Software Solutions & Services"],
  },
  "/en/services/software-solutions-services/data-integrity": {
    title: "Data Integrity",
    subtitle: "Ensure accuracy and consistency throughout the data lifecycle",
    description: "Data integrity is as critical as ever, from equipment and laboratory systems to automated systems and IT infrastructure. QbD Group offers customized expertise to help your company comply with the latest regulations.",
    contentKey: "data-integrity",
    contentTags: ["Software Solutions & Services"],
  },
  "/en/services/software-solutions-services/software-implementations": {
    title: "Software Implementations",
    subtitle: "Adopt and integrate software seamlessly into your business workflow",
    description: "Software implementations are essential for modern life science organizations to stay competitive. Our team of software experts ensures that your implementation project is successful, compliant, and meets the needs of your organization.",
    contentKey: "software-implementations",
    contentTags: ["Software Solutions & Services"],
  },
  "/en/services/software-solutions-services/it-infrastructure-qualification": {
    title: "IT Infrastructure Qualification",
    subtitle: "Demonstrate IT component fitness for GxP compliance",
    description: "In today's highly regulated environment, ensuring the reliability and compliance of IT infrastructure is a growing challenge. QbD Group offers IT infrastructure qualification expertise to help your company comply with the latest regulations.",
    contentKey: "it-infrastructure-qualification",
    contentTags: ["Software Solutions & Services", "Qualification & Validation"],
  },
  "/en/services/software-solutions-services/paperless-validation": {
    title: "Paperless Validation",
    subtitle: "Revolutionize your validation process with digital solutions",
    description: "Paperless validation offers a digital approach that revolutionizes the validation process in regulated industries. Say goodbye to paper-based documentation and hello to enhanced efficiency, accelerated validation, and improved data integrity.",
    contentKey: "paperless-validation",
    contentTags: ["Software Solutions & Services"],
  },
  "/en/services/software-solutions-services/software-validation": {
    title: "Software Validation & Central IT Systems",
    subtitle: "Expert CSV services for GxP-regulated environments",
    description: "Software validation remains as critical as ever, from automation and digitization to artificial intelligence and machine learning. QbD Group offers software validation and CSV expertise to help your company comply with the latest regulations.",
    contentKey: "software-validation",
    contentTags: ["Software Solutions & Services", "CSV"],
  },
  "/en/services/software-solutions-services/ifucare": {
    title: "IFUcare — Electronic IFU Solution",
    subtitle: "Electronic IFU Solution for Medical Devices & IVD",
    description: "IFUcare offers a seamless electronic IFU service for medical device and IVD manufacturers, empowering you to leave paper IFUs behind and transition to compliant, cost-effective electronic labeling.",
    contentKey: "ifucare",
    contentTags: ["Software Solutions & Services", "Medical Devices", "IVD"],
  },
  "/en/services/software-solutions-services/scilife": {
    title: "Scilife™ — Quality Management Software",
    subtitle: "Simplify Quality Management",
    description: "Scilife™ is a user-friendly, all-in-one Quality Management Software designed to streamline every aspect of your workflow — from document control and training to CAPAs and AI-driven KPIs.",
    contentKey: "scilife",
    contentTags: ["Software Solutions & Services", "Quality Assurance"],
  },
};

const SWOffering = () => {
  const location = useStableLocation();
  const [formOpen, setFormOpen] = useState(false);
  const [contactForm, setContactForm] = useState({
    firstName: "",
    lastName: "",
    company: "",
    email: "",
    country: "",
    message: "",
    consent: false,
  });
  const [contactErrors, setContactErrors] = useState<Record<string, string>>({});
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [openAiFaq, setOpenAiFaq] = useState<number | null>(0);


  const updateContactField = (field: keyof typeof contactForm, value: string | boolean) => {
    setContactForm((prev) => ({ ...prev, [field]: value }));
    if (contactErrors[field]) {
      setContactErrors((prev) => { const next = { ...prev }; delete next[field]; return next; });
    }
  };

  const handleContactSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const result = contactSchema.safeParse(contactForm);
    if (!result.success) {
      const errs: Record<string, string> = {};
      result.error.issues.forEach((err) => { errs[err.path[0] as string] = err.message; });
      setContactErrors(errs);
      return;
    }
    setContactErrors({});
    setContactSubmitted(true);
  };

  const lang = useLanguage();
  const prefix = langPrefix(lang);
  const servicesPath = localizeHref("/en/services", lang);
  const softwareSolutionsPath = localizeHref("/en/services/software-solutions-services", lang);

  const enPath = getOfferingPath(location.pathname) || location.pathname;
  const page =
    offeringPages[enPath] ??
    offeringPages[
      Object.keys(offeringPages).find((k) => k.toLowerCase() === enPath.toLowerCase()) ?? ""
    ];

  const parentService = getServiceBySlug("software-solutions-services");

  if (!page || !parentService) return <Navigate to={softwareSolutionsPath} replace />;

  // Hard draft: AI page is only viewable with ?preview=1
  const rawSearch = location.search || (typeof window !== "undefined" ? window.location.search : "");
  const isPreview = new URLSearchParams(rawSearch).get("preview") === "1";
  if (page.contentKey === "digitalization-AI" && typeof window !== "undefined" && !isPreview) {
    return <Navigate to={softwareSolutionsPath} replace />;
  }


  const serviceI18n = getServiceContent("software-solutions-services", lang);
  const displayParentService = serviceI18n
    ? { ...parentService, title: serviceI18n.title, formHeading: serviceI18n.formHeading, formSubtitle: serviceI18n.formSubtitle }
    : parentService;

  const esOverride = getSwOfferingI18n(page.contentKey, lang);
  const displayTitle = esOverride?.title ?? page.title;
  const displaySubtitle = esOverride?.subtitle ?? page.subtitle;
  const displayDescription = esOverride?.description ?? page.description;
  const displayFaqItems = esOverride?.faqItems ?? page.faqItems;

  const offeringData = swOfferingData[page.contentKey];
  const heroService = {
    ...parentService,
    title: displayTitle,
    subtitle: displaySubtitle,
    description: displayDescription,
    path: location.pathname,
    ...(offeringData?.whatImage ? { heroImage: offeringData.whatImage } : {}),
  };

  
  const pageOgImage = page.ogImage || DEFAULT_OG_IMAGE;
  const isAiPage = page.contentKey === "digitalization-AI";
  const metaTitle = isAiPage
    ? "AI Transformation Program for Pharma and MedTech | QbD Group"
    : `${displayTitle} — Software Solutions & Services | QbD Group`;
  const metaDesc = isAiPage
    ? "Move from scattered AI pilots to governed, inspection-ready AI adoption across every site. A six-phase program built by pharma industry specialists."
    : displayDescription.slice(0, 155);
  const pageUrl = `${SITE}${location.pathname}`;
  const relatedTags = page.contentTags || ["Software", "CSV", "Digitalization"];

  const data = swOfferingData[page.contentKey];

  return (
    <>
      <SEOHead title={metaTitle} description={metaDesc} canonical={pageUrl} ogImage={pageOgImage} noIndex={page.noIndex}>
        <script type="application/ld+json">
          {JSON.stringify({ "@context": "https://schema.org", "@type": "Service", name: isAiPage ? "AI Transformation Program for Pharma and MedTech" : displayTitle, description: isAiPage ? metaDesc : displayDescription, provider: { "@type": "Organization", name: "QbD Group", url: SITE }, url: pageUrl, isPartOf: { "@type": "Service", name: parentService.title, url: `${SITE}${parentService.path}` }, ...keyTakeawaySchema(heroTakeaway(displaySubtitle, displayDescription)) })}
        </script>
        {displayFaqItems && displayFaqItems.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({ "@context": "https://schema.org", "@type": "FAQPage", mainEntity: displayFaqItems.map((faq) => ({ "@type": "Question", name: faq.q, acceptedAnswer: { "@type": "Answer", text: faq.a } })) })}
          </script>
        )}
      </SEOHead>

      
      <BreadcrumbJsonLd
        items={[
          { name: lang === "es" ? "Servicios" : lang === "zh-cn" ? "服务" : "Services", path: servicesPath },
          { name: "Software Solutions & Services", path: softwareSolutionsPath },
          { name: displayTitle },
        ]}
      />

      <ScrollToTop />
      <Navbar logoOverride={DIVISION_LOGOS["software-solutions-services"]} />
      <CommercialBrowseSidebar />

      <main className="pb-16 sm:pb-0" style={{ '--service-color': parentService.color } as React.CSSProperties}>
        {isAiPage && (
          <div className="bg-amber-100 border-b border-amber-200 text-amber-900 text-sm font-medium text-center py-2 px-4">
            Internal review — this page is not indexed and only visible with ?preview=1
          </div>
        )}
        {page.contentKey !== "ai-transformation" && (
          <ServiceHero
            service={heroService}
            onCtaClick={() => setFormOpen(true)}
            hideDiscoverButton={!isAiPage}
            discoverButtonTarget={isAiPage ? "roadmap" : undefined}
            parentCrumbs={[
              { label: "Software Solutions & Services", to: softwareSolutionsPath },
            ]}
          />
        )}

        {page.contentKey === "digitalization-AI" && (
          <>


            {/* Credibility bar */}
            <section className="bg-surface-alt border-y border-border">
              <div className="container mx-auto px-6 lg:px-10 py-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-0">
                  {[
                    { value: "600+", label: "life sciences experts" },
                    { value: "14 sites", label: "largest active AI program scope" },
                    { value: "ISO/IEC 42001", label: "readiness built into the roadmap" },
                    { value: "FDA", label: "network of former FDA auditors" },
                  ].map((stat, i) => (
                    <div key={i} className="relative flex flex-col items-start lg:items-center px-0 lg:px-6">
                      <span className="text-lg font-bold text-brand-navy">{stat.value}</span>
                      <span className="text-sm text-ink-muted text-left lg:text-center mt-1">{stat.label}</span>
                      {i < 3 && <div className="hidden lg:block absolute right-0 top-1/2 -translate-y-1/2 h-10 w-px bg-border" />}
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Problem section */}
            <section className="bg-white py-16 sm:py-20">
              <div className="container mx-auto px-6 lg:px-10">
                <Reveal>
                <span className="inline-block text-xs font-semibold tracking-widest uppercase text-accent-coral mb-4">
                  THE PROBLEM
                </span>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-navy leading-tight tracking-tight mb-5">
                  Impressive pilots. No measurable change.
                </h2>
                <p className="text-base sm:text-lg text-ink-muted max-w-[720px] mb-12 leading-relaxed">
                  AI adoption in pharma happens bottom-up. Employees experiment on their own, tools multiply, and nobody owns the risk. You get distraction, inconsistent operations and exposure you cannot evidence to an inspector.
                </p>
                </Reveal>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-12">
                  {[
                    {
                      icon: Users,
                      title: "Uncontrolled adoption",
                      body: "Employees experiment with AI individually. Adoption is random, operations drift out of alignment and the organisation cannot see what is actually in use.",
                    },
                    {
                      icon: ShieldAlert,
                      title: "Security exposure",
                      body: "Company data moves through tools nobody approved. No data classification, no supplier qualification, no audit trail.",
                    },
                    {
                      icon: Scale,
                      title: "Compliance risk",
                      body: "AI reaches GxP processes without a validation approach, change control or documented oversight. That is a finding waiting to happen.",
                    },
                    {
                      icon: TrendingDown,
                      title: "Value that never lands",
                      body: "The pilots look good. Very few companies can then show reduced costs, increased revenue, faster cycle times or improved quality.",
                    },
                  ].map((card, i) => (
                    <Reveal key={i} delay={i * 60} className="bg-white border border-border rounded-xl p-6 lg:p-8">
                      <card.icon size={32} strokeWidth={1.5} aria-hidden="true" className="text-accent-coral mb-5" />
                      <h3 className="text-lg sm:text-xl font-semibold text-brand-navy mb-3">
                        {card.title}
                      </h3>
                      <p className="text-sm sm:text-base text-ink-muted leading-relaxed">
                        {card.body}
                      </p>
                    </Reveal>
                  ))}
                </div>
              </div>

              <div className="bg-brand-navy py-12">
                <div className="container mx-auto px-6 lg:px-10">
                  <p className="text-center text-white text-xl sm:text-2xl font-semibold max-w-[800px] mx-auto leading-snug">
                    AI capability moves faster than your governance. Every month without a framework adds systems you will have to remediate later.
                  </p>
                </div>
              </div>
            </section>

            {/* Program section */}
            <section className="bg-surface-alt py-16 sm:py-20">
              <div className="container mx-auto px-6 lg:px-10">
                <Reveal>
                <span className="inline-block text-xs font-semibold tracking-widest uppercase text-accent-teal mb-4">
                  THE PROGRAM
                </span>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-navy leading-tight tracking-tight mb-6">
                  Pharma process understanding, AI expertise, and field experience
                </h2>
                <div className="max-w-[780px] space-y-4 mb-12">
                  <p className="text-base sm:text-lg text-ink-muted leading-relaxed">
                    Our AI Transformation Program comes from the QbD Software Solutions and Services Center of Excellence. The team has hands-on pharma industry experience and has run AI adoption inside regulated environments. The approach keeps evolving with what we learn in the field.
                  </p>
                  <p className="text-base sm:text-lg text-ink-muted leading-relaxed">
                    This is not a parallel structure bolted onto your business. We adapt the program to the systems and controls you already run.
                  </p>
                </div>
                </Reveal>

                <div className="bg-white rounded-2xl border border-border p-6 lg:p-8">
                  <h3 className="text-lg sm:text-xl font-semibold text-brand-navy mb-6">
                    Built to integrate with what you already run
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[
                      { icon: ShieldCheck, text: "Quality Management System" },
                      { icon: Lock, text: "IT and cyber security governance" },
                      { icon: Server, text: "IT systems" },
                      { icon: Database, text: "Data integrity and privacy programs" },
                      { icon: Cpu, text: "Computerised systems lifecycle controls (SDLC)" },
                      { icon: Truck, text: "Supplier and vendor management" },
                      { icon: GraduationCap, text: "Training and competency" },
                      { icon: RefreshCw, text: "Change control, non-conformance and CAPA" },
                      { icon: Handshake, text: "Customer confidentiality obligations" },
                    ].map((tile, i) => (
                      <Reveal
                        key={i}
                        delay={i * 60}
                        className="flex items-center gap-3 bg-white border border-border rounded-xl p-5"
                      >
                        <tile.icon size={20} strokeWidth={1.5} aria-hidden="true" className="text-accent-teal shrink-0" />
                        <span className="text-[15px] font-semibold text-brand-navy leading-tight">
                          {tile.text}
                        </span>
                      </Reveal>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* Business outcomes section */}
            <section className="bg-white py-16 sm:py-20">
              <div className="container mx-auto px-6 lg:px-10">
                <Reveal>
                <span className="inline-block text-xs font-semibold tracking-widest uppercase text-accent-sky mb-4">
                  BUSINESS CASE
                </span>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-navy leading-tight tracking-tight mb-5">
                  Define the vision first. The value follows.
                </h2>
                <p className="text-base sm:text-lg text-ink-muted max-w-[720px] mb-12 leading-relaxed">
                  We start from a blueprint built on industry experience and adapt it to your objectives. These are the outcomes our clients target.
                </p>
                </Reveal>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
                  {[
                    {
                      accent: "--accent-teal",
                      title: "Reduce effort",
                      bullets: [
                        "Cut administrative and documentation effort",
                        "Reduce recurring deviations and manufacturing losses",
                        "Improve asset reliability",
                      ],
                    },
                    {
                      accent: "--accent-sky",
                      title: "Move faster",
                      bullets: [
                        "Accelerate investigations and technical assessments",
                        "Accelerate development, and responses to regulators and customers",
                        "Support regulatory activities with faster evidence retrieval",
                      ],
                    },
                    {
                      accent: "--accent-lime",
                      title: "See more",
                      bullets: [
                        "Improve access to internal knowledge",
                        "Improve data and process understanding",
                        "Improve capacity planning and supply chain visibility",
                      ],
                    },
                    {
                      accent: "--accent-yellow",
                      title: "Build capability",
                      bullets: [
                        "Improve training effectiveness",
                        "Establish role-based AI competency across every site",
                      ],
                    },
                  ].map((card, i) => (
                    <Reveal
                      key={i}
                      delay={i * 60}
                      className="bg-white border border-border rounded-xl p-6 lg:p-8"
                      style={{ borderTopWidth: 4, borderTopColor: `hsl(var(${card.accent}))` }}
                    >
                      <h3 className="text-lg sm:text-xl font-semibold text-brand-navy mb-4">
                        {card.title}
                      </h3>
                      <ul className="space-y-3">
                        {card.bullets.map((bullet, j) => (
                          <li key={j} className="flex items-start gap-3 text-sm sm:text-base text-ink-muted leading-relaxed">
                            <span
                              aria-hidden="true"
                              className="mt-2 w-1.5 h-1.5 shrink-0"
                              style={{ backgroundColor: `hsl(var(${card.accent}))` }}
                            />
                            {bullet}
                          </li>
                        ))}
                      </ul>
                    </Reveal>
                  ))}
                </div>
              </div>
            </section>

            {/* Governance section */}
            <section className="bg-surface-alt py-16 sm:py-20">
              <div className="container mx-auto px-6 lg:px-10">
                <Reveal>
                <span className="inline-block text-xs font-semibold tracking-widest uppercase text-accent-teal mb-4">
                  GOVERNANCE
                </span>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-navy leading-tight tracking-tight mb-5">
                  Someone has to own AI. We build that structure.
                </h2>
                <p className="text-base sm:text-lg text-ink-muted max-w-[720px] mb-12 leading-relaxed">
                  Most AI risk in regulated companies comes from the absence of an owner. We put the structure in place first, then the controls that make it work day to day.
                </p>
                </Reveal>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
                  {/* Left column — Structure */}
                  <Reveal className="bg-white border border-border rounded-xl p-6 lg:p-8">
                    <h3 className="text-lg sm:text-xl font-semibold text-brand-navy mb-6">
                      Structure we put in place
                    </h3>
                    <ol className="space-y-5">
                      {[
                        "Executive sponsor and AI steering committee, with a defined approval mandate",
                        "An AI Center of Excellence that carries the operational tasks",
                        "Trained global and site AI champions",
                        "A quality oversight program built into the model, not added later",
                      ].map((item, i) => (
                        <li key={i} className="flex items-start gap-4">
                          <span className="flex items-center justify-center w-7 h-7 rounded-full bg-accent-teal text-white text-sm font-semibold shrink-0">
                            {i + 1}
                          </span>
                          <span className="text-sm sm:text-base text-ink-muted leading-relaxed pt-0.5">
                            {item}
                          </span>
                        </li>
                      ))}
                    </ol>
                  </Reveal>

                  {/* Right column — Controls */}
                  <Reveal delay={60} className="bg-white border border-border rounded-xl p-6 lg:p-8">
                    <h3 className="text-lg sm:text-xl font-semibold text-brand-navy mb-6">
                      Controls we install
                    </h3>
                    <ul className="space-y-4">
                      {[
                        "Current-state assessment. Discovery of the AI already running across ERP, LIMS, MES and QMS",
                        "An AI use classification model, including uses your company prohibits",
                        "A formal use-case intake process with a defined approval gate",
                        "A controlled enterprise AI architecture that stops independent tool sprawl",
                        "Risk-based lifecycle assurance, so validation effort goes where the risk actually is",
                        "A role-based training program",
                        "A program metrics plan you can report to the board",
                      ].map((item, i) => (
                        <li key={i} className="flex items-start gap-3 text-sm sm:text-base text-ink-muted leading-relaxed">
                          <Check size={18} strokeWidth={2.5} aria-hidden="true" className="text-accent-teal shrink-0 mt-0.5" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </Reveal>
                </div>
              </div>
            </section>

            <AiRoadmapSection />

            {/* Differentiation section */}
            <section className="bg-brand-navy py-16 sm:py-20">
              <div className="container mx-auto px-6 lg:px-10">
                <Reveal>
                <span className="inline-block text-xs font-semibold tracking-widest uppercase text-accent-lime mb-4">
                  WHY QBD GROUP
                </span>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight tracking-tight mb-5">
                  Enterprise programme discipline. Industry people.
                </h2>
                <p className="text-base sm:text-lg text-white/80 max-w-[760px] mb-12 leading-relaxed">
                  You will see the large consultancies in this process. We compete in the same rooms. We win on where our people come from and what they have already had to defend in front of an inspector.
                </p>
                </Reveal>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-8">
                  {[
                    {
                      title: "We come out of the industry",
                      body: "Pharma and MedTech operations experience, not generic transformation methodology ported into your context afterwards.",
                    },
                    {
                      title: "Compliance is our home ground",
                      body: "One of Europe's larger computer system validation teams sits behind this program. Data integrity, GxP and inspection readiness are daily work here, not a workstream we staff up for.",
                    },
                    {
                      title: "Senior people stay on the account",
                      body: "Flexible scoping, direct access to the people running your program, no rotating delivery pyramid.",
                    },
                    {
                      title: "Tooling as well as advice",
                      body: "Scilife, our quality management platform, brings AI into the regulated workflow itself. Most consultancies can only advise.",
                    },
                  ].map((card, i) => (
                    <Reveal
                      key={i}
                      delay={i * 60}
                      className="bg-white/[0.08] border border-white/[0.15] rounded-xl p-8 text-white"
                    >
                      <h3 className="text-lg sm:text-xl font-semibold mb-3">
                        {card.title}
                      </h3>
                      <p className="text-sm sm:text-base leading-relaxed text-white/90">
                        {card.body}
                      </p>
                    </Reveal>
                  ))}
                </div>

                <Reveal className="border-l-4 border-accent-lime p-5">
                  <p className="text-[15px] text-white leading-relaxed">
                    Inspection readiness support on this program draws on our network of former FDA auditors.
                  </p>
                </Reveal>
              </div>
            </section>

            {/* Proof section */}
            <section className="bg-white py-16 sm:py-20">
              <div className="container mx-auto px-6 lg:px-10">
                <Reveal>
                <span className="inline-block text-xs font-semibold tracking-widest uppercase text-accent-teal mb-4">
                  PROOF
                </span>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-navy leading-tight tracking-tight mb-12">
                  What this looks like in practice
                </h2>
                </Reveal>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-12">
                  {[
                    {
                      metric: "6 weeks",
                      label: "US GO-LIVE",
                      id: "proof-card-us-go-live",
                      title: "Full eQMS live in six weeks",
                      body: "Implementation, configuration, training and validation delivered in six weeks using standard configuration blueprints and a pre-built validation package. Client name pending approval.",
                      toConfirm: true,
                    },
                    {
                      metric: "14",
                      label: "SITES",
                      id: "proof-card-14-sites",
                      title: "Corporate AI transformation program",
                      body: "A multi-site AI adoption program covering governance, use case portfolio and GxP assurance across fourteen manufacturing sites. Client name pending approval.",
                      toConfirm: true,
                    },
                    {
                      metric: "600+",
                      label: "EXPERTS",
                      id: "proof-card-capacity",
                      title: "Delivery capacity behind the program",
                      body: "Validation, quality, regulatory and IT specialists across Europe and the US, so the program scales with your site count instead of stalling on availability.",
                      toConfirm: false,
                    },
                  ].map((card, i) => (
                    <Reveal
                      key={i}
                      id={card.id}
                      delay={i * 60}
                      className={`relative scroll-mt-28 bg-white border rounded-xl p-8 ${
                        card.toConfirm
                          ? "border-2 border-dashed border-accent-yellow"
                          : "border-border"
                      }`}
                    >
                      {card.toConfirm && (
                        <span className="absolute top-4 right-4 inline-block px-2.5 py-1 bg-accent-yellow/15 text-brand-navy text-[10px] font-bold tracking-wider uppercase rounded">
                          TO CONFIRM
                        </span>
                      )}
                      <div className="text-[44px] font-extrabold text-brand-navy leading-none mb-3">
                        {card.metric}
                      </div>
                      <div className="text-xs font-semibold tracking-wider uppercase text-ink-muted mb-4">
                        {card.label}
                      </div>
                      <h3 className="text-lg sm:text-xl font-semibold text-brand-navy mb-3">
                        {card.title}
                      </h3>
                      <p className="text-sm sm:text-base text-ink-muted leading-relaxed">
                        {card.body}
                      </p>
                    </Reveal>
                  ))}
                </div>

                {/* Testimonial block */}
                <Reveal id="proof-testimonial" className="relative scroll-mt-28 bg-surface-alt rounded-xl p-10 border-2 border-dashed border-accent-yellow">
                  <span className="absolute top-4 right-4 inline-block px-2.5 py-1 bg-accent-yellow/15 text-brand-navy text-[10px] font-bold tracking-wider uppercase rounded">
                    TO CONFIRM
                  </span>
                    <div aria-hidden="true" className="text-5xl text-accent-teal leading-none mb-4">“</div>
                  <p className="text-[22px] font-medium text-brand-navy leading-snug mb-8">
                    Quote to be supplied and approved by the reference contact.
                  </p>
                  <div className="flex items-center gap-4">
                    <div
                      role="img"
                      aria-label="Placeholder for the reference contact portrait, to be supplied"
                      className="w-12 h-12 rounded-full bg-brand-navy/10 flex items-center justify-center text-brand-navy/50 text-xs font-semibold"
                    >
                      IMG
                    </div>
                    <div>
                      <p className="text-base font-semibold text-brand-navy">Reference contact</p>
                      <p className="text-sm text-ink-muted">Chief Quality and Compliance Officer, client name to be confirmed</p>
                    </div>
                  </div>
                </Reveal>
              </div>
            </section>



            <ReviewItemsPanel items={AI_REVIEW_ITEMS} />
          </>
        )}

        {page.contentKey === "ai-transformation" && (
          <AITransformationContent parentService={parentService} onCtaClick={() => setFormOpen(true)} />
        )}


        {data && page.contentKey !== "digitalization-AI" && page.contentKey !== "ai-transformation" && (
          <GenericOfferingContent
            data={esOverride ? {
              ...data,
              whatTitle: esOverride.whatTitle,
              whatParagraphs: esOverride.whatParagraphs,
              whatImageAlt: esOverride.whatImageAlt ?? data.whatImageAlt,
              whyTitle: esOverride.whyTitle,
              whyIntro: esOverride.whyIntro ?? data.whyIntro,
              whyBenefits: (data.whyBenefits ?? []).map((b, i) => esOverride.whyBenefits[i] ? { ...b, title: esOverride.whyBenefits[i].title, text: esOverride.whyBenefits[i].text } : b),
              whyImageAlt: esOverride.whyImageAlt ?? data.whyImageAlt,
              helpCards: esOverride.helpCards && data.helpCards ? {
                ...data.helpCards,
                heading: esOverride.helpCards.heading,
                intro: esOverride.helpCards.intro,
                cards: data.helpCards.cards.map((c, i) => esOverride.helpCards!.cards[i] ? { ...c, title: esOverride.helpCards!.cards[i].title, text: esOverride.helpCards!.cards[i].text } : c),
              } : data.helpCards,
              processTitle: esOverride.processTitle ?? data.processTitle,
              processIntro: esOverride.processIntro ?? data.processIntro,
              processSteps: esOverride.processSteps ? data.processSteps?.map((s, i) => esOverride.processSteps![i] ? { ...s, title: esOverride.processSteps![i].title, text: esOverride.processSteps![i].text } : s) : data.processSteps,
              processImageAlt: esOverride.processImageAlt ?? data.processImageAlt,
              extraSections: esOverride.extraSections ?? data.extraSections,
              externalCtaLabel: esOverride.externalCtaLabel ?? data.externalCtaLabel,
              partnerTitle: esOverride.partnerTitle,
              partnerIntro: esOverride.partnerIntro,
              partnerBullets: esOverride.partnerBullets,
              partnerImageAlt: esOverride.partnerImageAlt ?? data.partnerImageAlt,
            } : data}
            onCtaClick={() => setFormOpen(true)}
            hidePartnerSection
          />
        )}

        {page.contentKey !== "digitalization-AI" && page.contentKey !== "ai-transformation" && (
          <ServiceWhyQbd
            service={parentService}
            onCtaClick={() => setFormOpen(true)}
            partnerOverride={esOverride ? { intro: esOverride.partnerIntro, bullets: esOverride.partnerBullets } : data ? { intro: data.partnerIntro, bullets: data.partnerBullets } : undefined}
          />
        )}

        {displayFaqItems && displayFaqItems.length > 0 && (
          <section className="py-16 sm:py-20 bg-muted/30">
            <div className="container mx-auto px-6 lg:px-10 max-w-5xl">
              <div className="flex items-center gap-3 mb-2">
                <span className="w-6 h-0.5 rounded-full" style={{ backgroundColor: parentService.color }} />
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{({ en: "Digital systems and compliance", es: "Sistemas digitales y normativa", "zh-cn": "数字系统合规实践" }[lang])}</p>
              </div>
              <h2 className="text-lg sm:text-xl font-semibold text-foreground mb-6">
                {lang === "es" ? "Preguntas frecuentes" : lang === "zh-cn" ? "常见问题" : "Frequently Asked Questions"}
              </h2>
              <Accordion type="single" collapsible className="space-y-2">
                {displayFaqItems.map((faq, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} className="bg-background border border-border/40 rounded-lg px-5">
                    <AccordionTrigger className="text-sm font-medium text-foreground hover:no-underline">
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </section>
        )}

        {page.contentKey !== "ai-transformation" && (
          <ServiceRelatedContent service={parentService} contentTags={relatedTags} pinnedSlugs={page.pinnedSlugs} />
        )}

        <Dialog open={formOpen} onOpenChange={setFormOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogTitle className="sr-only">{displayParentService.formHeading}</DialogTitle>
            <WebinarRegistrationForm
              registrationUrl={null}
              title={displayParentService.title}
              hubspotFormId={page.contentKey === "ai-transformation" ? "3963ceac-3c61-4c9e-879c-a69911b81de5" : parentService.hubspotFormId}
              heading={displayParentService.formHeading}
              subtitle={displayParentService.formSubtitle}
            />

          </DialogContent>
        </Dialog>

        {page.contentKey !== "ai-transformation" && (
          <ContactCTA
            onContactClick={() => setFormOpen(true)}
            title={displayParentService.formHeading}
            description={displayParentService.formSubtitle}
          />
        )}
      </main>

      <Footer />
    </>
  );
};

export default SWOffering;
